import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory2 as InventoryIcon,
  PendingActions as PendingActionsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Shield as LostIcon,
  Warehouse as WarehouseIcon,
  Print as PrintIcon,
} from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { productsService, stockUnitsService, integrationsService } from '../services/api'
import { productVariantsService } from '../services/productVariantsService'
import { useNotification } from '../contexts/NotificationContext'

const DEFAULT_PAGE_SIZE = 25

const formatCurrency = (value) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number.isFinite(Number(value)) ? Number(value) : 0)

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '—')

const initialFilters = {
  product: null,
  variant: null,
  inStock: null,
  isLost: null,
}

const createEmptyBulkRow = () => ({
  id: Math.random().toString(36).slice(2),
  product: null,
  variant: null,
  name: '',
  quantityOrigin: 1,
  quantity: 1,
  buyPrice: '',
  additionnalPrice: '',
  emplacement: '',
  expirationDate: '',
  inStock: true,
  isLost: false,
  quantityCount: 1,
  variantOptions: [],
})

const Stock = () => {
  const { showNotification } = useNotification()
  const [loading, setLoading] = useState(false)
  const [units, setUnits] = useState([])
  const [pagination, setPagination] = useState({ page: 1, total: 0, perPage: DEFAULT_PAGE_SIZE })
  const [searchValue, setSearchValue] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters] = useState(initialFilters)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [reservedBufferOpen, setReservedBufferOpen] = useState(false)
  const [reservedBufferItems, setReservedBufferItems] = useState([])
  const [reservedBufferLoading, setReservedBufferLoading] = useState(false)

  const [productOptions, setProductOptions] = useState([])
  const [productSearchTerm, setProductSearchTerm] = useState('')
  const [productLoading, setProductLoading] = useState(false)
  const [variantOptions, setVariantOptions] = useState([])
  const [variantLoading, setVariantLoading] = useState(false)
  const [modalVariantOptions, setModalVariantOptions] = useState([])
  const [modalVariantLoading, setModalVariantLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('single') // single | bulk | edit
  const [activeTab, setActiveTab] = useState('single')
  const [modalLoading, setModalLoading] = useState(false)
  const [quantityCount, setQuantityCount] = useState(1)
  const [formValues, setFormValues] = useState({
    product: null,
    variant: null,
    name: '',
    quantityOrigin: 1,
    quantity: 1,
    buyPrice: '',
    additionnalPrice: '',
    emplacement: '',
    expirationDate: '',
    inStock: true,
    isLost: false,
    uuid: '',
  })
  const [bulkRows, setBulkRows] = useState([createEmptyBulkRow()])
  const [editingUnit, setEditingUnit] = useState(null)

  const fetchUnits = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.perPage,
        search: searchValue || undefined,
        productId: filters.product ? filters.product.id : undefined,
        productVariantId: filters.variant ? filters.variant.id : undefined,
        inStock: filters.inStock !== null ? filters.inStock : undefined,
        isLost: filters.isLost !== null ? filters.isLost : undefined,
      }
      const response = await stockUnitsService.list(params)
      const data = response.data
      setUnits(data.data || [])
      setPagination((prev) => ({
        ...prev,
        total: data.meta?.total || 0,
        perPage: data.meta?.per_page || prev.perPage,
        page: data.meta?.current_page || prev.page,
      }))
    } catch (error) {
      console.error('Erreur chargement unités:', error)
      showNotification('Erreur lors du chargement du stock', 'error')
    } finally {
      setLoading(false)
    }
  }, [filters.inStock, filters.isLost, filters.product, filters.variant, pagination.page, pagination.perPage, searchValue, showNotification])

  const fetchSummary = useCallback(async () => {
    if (!filters.product) {
      setSummary(null)
      return
    }
    setSummaryLoading(true)
    try {
      const params = {
        productId: filters.product.id,
      }
      if (filters.variant) {
        params.productVariantId = filters.variant.id
      }
      const response = await stockUnitsService.summary(params)
      setSummary(response.data)
    } catch (error) {
      console.error('Erreur chargement résumé stock:', error)
    } finally {
      setSummaryLoading(false)
    }
  }, [filters.product, filters.variant])

  useEffect(() => {
    fetchUnits()
  }, [fetchUnits])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPagination((prev) => ({ ...prev, page: 1 }))
      setSearchValue(searchInput.trim())
    }
  }

  const handleSearchBlur = () => {
    // Optionnel, garder pour future extension
  }

  const handleRefresh = () => {
    fetchUnits()
    fetchSummary()
  }

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage + 1,
    }))
  }

  const handleRowsPerPageChange = (event) => {
    setPagination({
      page: 1,
      perPage: parseInt(event.target.value, 10),
      total: pagination.total,
    })
  }

  const loadProducts = useCallback(
    async (keyword = '') => {
      setProductLoading(true)
      try {
        const params = { limit: 100, page: 1 }
        const response = keyword
          ? await productsService.search(keyword, params)
          : await productsService.getAll(params)
        const data = response.data.products || response.data || []
        setProductOptions(data)
      } catch (error) {
        console.error('Erreur chargement produits:', error)
      } finally {
        setProductLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  useEffect(() => {
    if (productSearchTerm === undefined) return
    const handler = setTimeout(() => {
      loadProducts(productSearchTerm)
    }, 300)
    return () => clearTimeout(handler)
  }, [productSearchTerm, loadProducts])

  const loadVariants = useCallback(async (productId) => {
    if (!productId) {
      setVariantOptions([])
      return
    }
    setVariantLoading(true)
    try {
      const response = await productVariantsService.getByProduct(productId)
      setVariantOptions(response.data || [])
    } catch (error) {
      console.error('Erreur chargement variantes:', error)
      setVariantOptions([])
    } finally {
      setVariantLoading(false)
    }
  }, [])

  const loadVariantsForModal = useCallback(async (productId) => {
    if (!productId) {
      setModalVariantOptions([])
      return
    }
    setModalVariantLoading(true)
    try {
      const response = await productVariantsService.getByProduct(productId)
      setModalVariantOptions(response.data || [])
    } catch (error) {
      console.error('Erreur chargement variantes (modal):', error)
      setModalVariantOptions([])
    } finally {
      setModalVariantLoading(false)
    }
  }, [])

  useEffect(() => {
    if (filters.product) {
      loadVariants(filters.product.id)
    } else {
      setVariantOptions([])
    }
  }, [filters.product, loadVariants])

  useEffect(() => {
    if (modalMode === 'edit' || modalMode === 'single') {
      const productId = formValues.product?.id
      if (productId) {
        loadVariantsForModal(productId)
      } else {
        setModalVariantOptions([])
      }
    } else {
      setModalVariantOptions([])
    }
  }, [formValues.product, loadVariantsForModal, modalMode])

  const resetForm = useCallback(() => {
    setFormValues({
      product: null,
      variant: null,
      name: '',
      quantityOrigin: 1,
      quantity: 1,
      buyPrice: '',
      additionnalPrice: '',
      emplacement: '',
      expirationDate: '',
      inStock: true,
      isLost: false,
      uuid: '',
    })
    setQuantityCount(1)
    setBulkRows([createEmptyBulkRow()])
    setEditingUnit(null)
    setModalVariantOptions([])
  }, [])

  const openCreateModal = () => {
    resetForm()
    setModalMode('single')
    setActiveTab('single')
    setModalOpen(true)
  }

  const openBulkModal = () => {
    resetForm()
    setModalMode('bulk')
    setActiveTab('bulk')
    setModalOpen(true)
  }

  const openEditModal = async (unit) => {
    resetForm()
    setEditingUnit(unit)
    setModalMode('edit')
    setActiveTab('edit')
    setModalOpen(true)
    const product = unit.product || null
    const variant = unit.productVariant || null
    if (product) {
      await loadVariantsForModal(product.id)
    }
    setFormValues({
      product,
      variant,
      name: unit.name,
      quantityOrigin: unit.quantityOrigin,
      quantity: unit.quantity,
      buyPrice: unit.buyPrice,
      additionnalPrice: unit.additionnalPrice,
      emplacement: unit.emplacement,
      expirationDate: unit.expirationDate ? dayjs(unit.expirationDate).format('YYYY-MM-DD') : '',
      inStock: unit.inStock,
      isLost: unit.isLost,
      uuid: unit.uuid || '',
    })
  }

  const closeModal = () => {
    setModalOpen(false)
    setActiveTab('single')
  }

  const handleSingleSubmit = async () => {
    if (!formValues.product) {
      showNotification('Veuillez sélectionner un produit', 'warning')
      return
    }

    if (!formValues.emplacement) {
      showNotification('Veuillez indiquer un emplacement', 'warning')
      return
    }

    const payload = {
      productId: formValues.product.id,
      productVariantId: formValues.variant ? formValues.variant.id : undefined,
      name: formValues.name,
      quantityOrigin: Number(formValues.quantityOrigin) || 0,
      quantity: Number(formValues.quantity) || 0,
      buyPrice: Number(formValues.buyPrice) || 0,
      additionnalPrice: Number(formValues.additionnalPrice) || 0,
      emplacement: formValues.emplacement,
      expirationDate: formValues.expirationDate || null,
      inStock: formValues.inStock,
      isLost: formValues.isLost,
      uuid: formValues.uuid || undefined,
    }

    try {
      setModalLoading(true)
      if (modalMode === 'edit' && editingUnit) {
        await stockUnitsService.update(editingUnit.id, payload)
        showNotification('Unité mise à jour', 'success')
      } else {
        await stockUnitsService.create({
          ...payload,
          quantityCount: quantityCount,
        })
        showNotification(
          quantityCount > 1 ? `${quantityCount} unités créées` : 'Unité créée',
          'success'
        )
      }
      closeModal()
      fetchUnits()
      fetchSummary()
    } catch (error) {
      console.error('Erreur enregistrement unité:', error)
      const message = error.response?.data?.message || 'Erreur lors de l’enregistrement'
      showNotification(message, 'error')
    } finally {
      setModalLoading(false)
    }
  }

  const handleBulkSubmit = async () => {
    const sanitizedRows = bulkRows
      .map((row) => ({
        ...row,
        productId: row.product?.id,
        productVariantId: row.variant?.id,
        quantityCount: Number(row.quantityCount) || 1,
        quantityOrigin: Number(row.quantityOrigin) || 0,
        quantity: Number(row.quantity) || 0,
        buyPrice: Number(row.buyPrice) || 0,
        additionnalPrice: Number(row.additionnalPrice) || 0,
      }))
      .filter(
        (row) =>
          row.productId &&
          row.emplacement &&
          row.quantityCount > 0
      )

    if (sanitizedRows.length === 0) {
      showNotification('Ajoutez au moins une ligne complète à importer', 'warning')
      return
    }

    try {
      setModalLoading(true)
      await stockUnitsService.bulkCreate(
        sanitizedRows.map((row) => ({
          productId: row.productId,
          productVariantId: row.productVariantId,
          name: row.name,
          quantityOrigin: row.quantityOrigin,
          quantity: row.quantity,
          buyPrice: row.buyPrice,
          additionnalPrice: row.additionnalPrice,
          emplacement: row.emplacement,
          expirationDate: row.expirationDate || null,
          inStock: row.inStock,
          isLost: row.isLost,
          quantityCount: row.quantityCount,
        }))
      )
      showNotification(`${sanitizedRows.length} ligne(s) importée(s)`, 'success')
      closeModal()
      fetchUnits()
      fetchSummary()
    } catch (error) {
      console.error('Erreur import unités:', error)
      const message = error.response?.data?.message || 'Erreur lors de l’import'
      showNotification(message, 'error')
    } finally {
      setModalLoading(false)
    }
  }

  const handleDeleteUnit = async (unit) => {
    if (!window.confirm(`Supprimer l’unité ${unit.uuid || unit.name} ?`)) return
    try {
      await stockUnitsService.delete(unit.id)
      showNotification('Unité supprimée', 'success')
      fetchUnits()
      fetchSummary()
    } catch (error) {
      console.error('Erreur suppression unité:', error)
      const message = error.response?.data?.message || 'Impossible de supprimer l’unité'
      showNotification(message, 'error')
    }
  }

  const handleToggleLost = async (unit) => {
    try {
      await stockUnitsService.update(unit.id, { isLost: !unit.isLost })
      showNotification(
        unit.isLost ? 'Unité retournée dans le stock' : 'Unité marquée comme perdue',
        unit.isLost ? 'info' : 'warning'
      )
      fetchUnits()
      fetchSummary()
    } catch (error) {
      console.error('Erreur mise à jour état perdu:', error)
      showNotification('Erreur lors de la mise à jour du statut', 'error')
    }
  }

  const handleToggleInStock = async (unit) => {
    try {
      await stockUnitsService.update(unit.id, { inStock: !unit.inStock })
      showNotification(
        unit.inStock ? 'Unité marquée comme hors dépôt' : 'Unité disponible sur place',
        'success'
      )
      fetchUnits()
      fetchSummary()
    } catch (error) {
      console.error('Erreur mise à jour disponibilité:', error)
      showNotification('Erreur lors de la mise à jour du stock', 'error')
    }
  }

  const handlePrintLabel = async (unit) => {
    try {
      await stockUnitsService.print(unit.id)
      showNotification('Impression lancée...', 'success')
    } catch (error) {
      console.error('Erreur impression étiquette:', error)
      showNotification('Erreur lors de l\'impression', 'error')
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'product' ? { variant: null } : null),
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleBulkRowChange = (rowId, field, value) => {
    setBulkRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row
        if (field === 'product') {
          if (value?.id) {
            void handleBulkRowVariantLoad(row.id, value.id)
          }
          return {
            ...row,
            product: value,
            variant: null,
            variantOptions: value?.variantOptions || [],
          }
        }
        return { ...row, [field]: value }
      })
    )
  }

  const handleBulkRowVariantLoad = async (rowId, productId) => {
    if (!productId) return []
    try {
      const response = await productVariantsService.getByProduct(productId)
      const options = response.data || []
      setBulkRows((prev) =>
        prev.map((row) => (row.id === rowId ? { ...row, variantOptions: options } : row))
      )
      return options
    } catch (error) {
      console.error('Erreur chargement variantes pour import:', error)
      return []
    }
  }

  const addBulkRow = () => {
    setBulkRows((prev) => [...prev, createEmptyBulkRow()])
  }

  const removeBulkRow = (rowId) => {
    setBulkRows((prev) => prev.filter((row) => row.id !== rowId))
  }

  const totalUnits = useMemo(() => pagination.total || 0, [pagination.total])

  const handleSyncPrestashop = async () => {
    if (!filters.product) return

    setSyncLoading(true)
    try {
      const payload = {
        productId: filters.product.id,
        variantId: filters.variant ? filters.variant.id : undefined,
      }
      await integrationsService.syncStock(payload)
      showNotification('Stock synchronisé avec PrestaShop', 'success')
    } catch (error) {
      console.error('Erreur sync PrestaShop:', error)
      const message = error.response?.data?.message || 'Erreur lors de la synchronisation'
      showNotification(message, 'error')
    } finally {
      setSyncLoading(false)
    }
  }

  const handleSyncAll = async () => {
    if (!window.confirm('Voulez-vous synchroniser TOUT le stock vers PrestaShop ? Cette opération peut prendre du temps.')) return

    setSyncLoading(true)
    try {
      const response = await integrationsService.syncAllStock()
      const { success, failed } = response.data.details || {}
      showNotification(`Sync terminée : ${success} succès, ${failed} échecs`, 'success')
    } catch (error) {
      console.error('Erreur sync globale:', error)
      showNotification('Erreur lors de la synchronisation globale', 'error')
    } finally {
      setSyncLoading(false)
    }
  }

  const renderSummaryCard = (title, value, color, icon = null) => (
    <Card sx={{ flex: 1, minWidth: 220 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          {icon}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 600, color }}>
              {summaryLoading ? <CircularProgress size={24} /> : value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarehouseIcon fontSize="large" />
            Gestion du stock unitaire
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {totalUnits} unité{totalUnits > 1 ? 's' : ''} référencée{totalUnits > 1 ? 's' : ''} •
            Stock tampon recalculé automatiquement selon les commandes en cours
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<PendingActionsIcon />}
            onClick={async () => {
              setReservedBufferOpen(true)
              setReservedBufferLoading(true)
              try {
                const res = await stockUnitsService.getReservedBuffer()
                setReservedBufferItems(res.data?.items ?? [])
              } catch (err) {
                showNotification('Erreur lors du chargement du stock tampon', 'error')
                setReservedBufferItems([])
              } finally {
                setReservedBufferLoading(false)
              }
            }}
          >
            Voir le stock tampon
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleSyncAll}
            disabled={syncLoading}
          >
            {syncLoading ? 'Sync...' : 'Synchroniser Tout'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={openBulkModal}
          >
            Import / Création de masse
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
          >
            Ajouter une unité
          </Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                placeholder="Rechercher (nom, produit, UUID, emplacement...)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={productOptions}
                loading={productLoading}
                getOptionLabel={(option) => option?.name || ''}
                value={filters.product}
                onChange={(_, value) => handleFilterChange('product', value)}
                onInputChange={(_, value) => setProductSearchTerm(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produit"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {productLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={variantOptions}
                loading={variantLoading}
                getOptionLabel={(option) => option?.name || ''}
                value={filters.variant}
                onChange={(_, value) => handleFilterChange('variant', value)}
                disabled={!filters.product}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Variante"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {variantLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.inStock === true}
                    onChange={(_, checked) =>
                      handleFilterChange('inStock', checked ? true : null)
                    }
                  />
                }
                label="En stock sur site"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.inStock === false}
                    onChange={(_, checked) =>
                      handleFilterChange('inStock', checked ? false : null)
                    }
                    color="warning"
                  />
                }
                label="En transit / hors site"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.isLost === true}
                    onChange={(_, checked) =>
                      handleFilterChange('isLost', checked ? true : null)
                    }
                    color="error"
                  />
                }
                label="Unités perdues"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.isLost === false}
                    onChange={(_, checked) =>
                      handleFilterChange('isLost', checked ? false : null)
                    }
                    color="success"
                  />
                }
                label="Uniquement unités actives"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {summary && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          {renderSummaryCard(
            'Unités totales',
            summary.totalUnits ?? 0,
            'primary.main',
            <InventoryIcon color="primary" />
          )}
          {renderSummaryCard(
            'Disponibles sur site',
            summary.inStockUnits ?? 0,
            'success.light',
            <WarehouseIcon color="success" />
          )}
          {renderSummaryCard(
            'Réservées (commandes)',
            summary.reserved ?? 0,
            'warning.light',
            <LostIcon color="warning" />
          )}
          {renderSummaryCard(
            'Stock tampon disponible',
            summary.available ?? 0,
            'info.light',
            <InventoryIcon color="info" />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Envoyer le stock disponible (Stock tampon) vers PrestaShop via le module Inkland Sync">
              <Button
                variant="outlined"
                color="info"
                startIcon={<RefreshIcon />}
                onClick={handleSyncPrestashop}
                disabled={syncLoading || !filters.product}
                sx={{ height: 'fit-content', py: 2 }}
              >
                {syncLoading ? 'Sync...' : 'Sync PrestaShop'}
              </Button>
            </Tooltip>
          </Box>
        </Stack>
      )}

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>UUID</TableCell>
                <TableCell>Produit</TableCell>
                <TableCell>Variante</TableCell>

                <TableCell>Emplacement</TableCell>
                <TableCell align="right">Qté origine</TableCell>
                <TableCell align="right">Qté</TableCell>
                <TableCell align="right">Prix achat</TableCell>
                <TableCell align="right">Supplément</TableCell>
                <TableCell>Disponibilité</TableCell>
                <TableCell>Expiration</TableCell>
                <TableCell>Créée le</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={3}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Chargement des unités...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : units.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Aucun résultat avec ces filtres.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                units.map((unit) => (
                  <TableRow key={unit.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{unit.uuid || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID #{unit.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {unit.product?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {unit.productVariant?.name || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip label={unit.emplacement || 'N/A'} size="small" />
                    </TableCell>
                    <TableCell align="right">{unit.quantityOrigin}</TableCell>
                    <TableCell align="right">{unit.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(unit.buyPrice)}</TableCell>
                    <TableCell align="right">{formatCurrency(unit.additionnalPrice)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={unit.inStock ? 'Sur site' : 'Hors site'}
                          size="small"
                          color={unit.inStock ? 'success' : 'warning'}
                        />
                        {!!unit.isLost && (
                          <Chip
                            label="Perdu"
                            size="small"
                            color="error"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(unit.expirationDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(unit.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Imprimer">
                          <span>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => handlePrintLabel(unit)}
                            >
                              <PrintIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditModal(unit)}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={unit.isLost ? 'Retirer le statut perdu' : 'Marquer comme perdu'}>
                          <span>
                            <IconButton
                              size="small"
                              color={unit.isLost ? 'success' : 'warning'}
                              onClick={() => handleToggleLost(unit)}
                            >
                              <LostIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={unit.inStock ? 'Marquer hors site' : 'Marquer en stock'}>
                          <span>
                            <IconButton
                              size="small"
                              color={unit.inStock ? 'info' : 'success'}
                              onClick={() => handleToggleInStock(unit)}
                            >
                              <InventoryIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteUnit(unit)}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={Math.max(pagination.page - 1, 0)}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Unités par page"
        />
      </Card>

      <Dialog
        open={modalOpen}
        onClose={!modalLoading ? closeModal : undefined}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {modalMode === 'edit'
              ? 'Modifier une unité'
              : activeTab === 'bulk'
                ? 'Import / création de masse'
                : 'Ajouter des unités'}
          </Typography>
        </DialogTitle>
        {modalMode !== 'edit' && (
          <Tabs
            value={modalMode}
            onChange={(_, value) => {
              setModalMode(value)
              setActiveTab(value)
            }}
            sx={{ px: 3 }}
          >
            <Tab label="Création rapide" value="single" />
            <Tab label="Import / masse" value="bulk" />
          </Tabs>
        )}
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {(modalMode === 'single' || modalMode === 'edit') && (
            <Box component="form" sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={productOptions}
                    loading={productLoading}
                    getOptionLabel={(option) => option?.name || ''}
                    value={formValues.product}
                    onChange={(_, value) => {
                      setFormValues((prev) => ({ ...prev, product: value, variant: null }))
                      if (value?.id) {
                        loadVariantsForModal(value.id)
                      } else {
                        setModalVariantOptions([])
                      }
                    }}
                    onInputChange={(_, value) => setProductSearchTerm(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Produit"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {productLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={modalVariantOptions}
                    loading={modalVariantLoading}
                    getOptionLabel={(option) => option?.name || ''}
                    value={formValues.variant}
                    onChange={(_, value) => setFormValues((prev) => ({ ...prev, variant: value }))}
                    disabled={!formValues.product}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Variante (optionnel)"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {modalVariantLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="UUID (optionnel)"
                    value={formValues.uuid}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, uuid: e.target.value }))}
                    placeholder="UUID généré automatiquement si vide"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Quantité origine"
                    type="number"
                    value={formValues.quantityOrigin}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, quantityOrigin: e.target.value }))
                    }
                    inputProps={{ step: '0.01' }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Quantité disponible"
                    type="number"
                    value={formValues.quantity}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, quantity: e.target.value }))
                    }
                    inputProps={{ step: '0.01' }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Emplacement"
                    value={formValues.emplacement}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, emplacement: e.target.value }))
                    }
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Prix d’achat"
                    type="number"
                    value={formValues.buyPrice}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, buyPrice: e.target.value }))
                    }
                    inputProps={{ step: '0.01' }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Supplément prix"
                    type="number"
                    value={formValues.additionnalPrice}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, additionnalPrice: e.target.value }))
                    }
                    inputProps={{ step: '0.01' }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Date d’expiration"
                    type="date"
                    value={formValues.expirationDate}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, expirationDate: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formValues.inStock}
                        onChange={(_, checked) =>
                          setFormValues((prev) => ({ ...prev, inStock: checked }))
                        }
                      />
                    }
                    label="Disponible sur site"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formValues.isLost}
                        onChange={(_, checked) =>
                          setFormValues((prev) => ({ ...prev, isLost: checked }))
                        }
                        color="error"
                      />
                    }
                    label="Marquer comme perdu"
                  />
                </Grid>
                {modalMode !== 'edit' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Nombre d’unités à créer"
                      type="number"
                      value={quantityCount}
                      onChange={(e) => setQuantityCount(Math.max(1, Number(e.target.value) || 1))}
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {modalMode === 'bulk' && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {bulkRows.map((row, index) => (
                <Card key={row.id} variant="outlined">
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Ligne {index + 1}
                      </Typography>
                      <Button
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => removeBulkRow(row.id)}
                        disabled={bulkRows.length === 1}
                      >
                        Supprimer
                      </Button>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={productOptions}
                          loading={productLoading}
                          getOptionLabel={(option) => option?.name || ''}
                          value={row.product}
                          onChange={(_, value) => handleBulkRowChange(row.id, 'product', value)}
                          onInputChange={(_, value) => setProductSearchTerm(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Produit"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {productLoading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={row.variantOptions || []}
                          getOptionLabel={(option) => option?.name || ''}
                          value={row.variant}
                          onChange={(_, value) => handleBulkRowChange(row.id, 'variant', value)}
                          disabled={!row.product}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Variante"
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Quantité origine"
                          type="number"
                          value={row.quantityOrigin}
                          onChange={(e) =>
                            handleBulkRowChange(row.id, 'quantityOrigin', e.target.value)
                          }
                          inputProps={{ step: '0.01' }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Quantité"
                          type="number"
                          value={row.quantity}
                          onChange={(e) => handleBulkRowChange(row.id, 'quantity', e.target.value)}
                          inputProps={{ step: '0.01' }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Prix achat"
                          type="number"
                          value={row.buyPrice}
                          onChange={(e) => handleBulkRowChange(row.id, 'buyPrice', e.target.value)}
                          inputProps={{ step: '0.01' }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Supplément prix"
                          type="number"
                          value={row.additionnalPrice}
                          onChange={(e) =>
                            handleBulkRowChange(row.id, 'additionnalPrice', e.target.value)
                          }
                          inputProps={{ step: '0.01' }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Emplacement"
                          value={row.emplacement}
                          onChange={(e) =>
                            handleBulkRowChange(row.id, 'emplacement', e.target.value)
                          }
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Date d’expiration"
                          type="date"
                          value={row.expirationDate}
                          onChange={(e) =>
                            handleBulkRowChange(row.id, 'expirationDate', e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.inStock}
                              onChange={(_, checked) =>
                                handleBulkRowChange(row.id, 'inStock', checked)
                              }
                            />
                          }
                          label="Disponible sur site"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isLost}
                              onChange={(_, checked) =>
                                handleBulkRowChange(row.id, 'isLost', checked)
                              }
                              color="error"
                            />
                          }
                          label="Marquer comme perdu"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Nombre d’unités à créer"
                          type="number"
                          value={row.quantityCount}
                          onChange={(e) =>
                            handleBulkRowChange(
                              row.id,
                              'quantityCount',
                              Math.max(1, Number(e.target.value) || 1)
                            )
                          }
                          inputProps={{ min: 1 }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addBulkRow}
                >
                  Ajouter une ligne
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={modalLoading}>
            Annuler
          </Button>
          <Button
            onClick={modalMode === 'bulk' ? handleBulkSubmit : handleSingleSubmit}
            variant="contained"
            disabled={modalLoading}
            startIcon={modalLoading ? <CircularProgress size={16} /> : undefined}
          >
            {modalMode === 'bulk' ? 'Importer' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reservedBufferOpen}
        onClose={() => setReservedBufferOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PendingActionsIcon />
          Stock tampon
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Produits dont la quantité est réservée par des commandes en cours (non expédiées). Ces quantités sont en stock mais non utilisables pour de nouvelles ventes.
          </Typography>
          {reservedBufferLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reservedBufferItems.length === 0 ? (
            <Typography color="text.secondary">Aucun produit en stock tampon.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produit</TableCell>
                    <TableCell>Variante</TableCell>
                    <TableCell align="right">Quantité réservée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservedBufferItems.map((row, idx) => (
                    <TableRow key={`${row.productId}-${row.productVariantId ?? 'p'}-${idx}`}>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{row.variantName}</TableCell>
                      <TableCell align="right">{row.reserved}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReservedBufferOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Stock
